// @ts-nocheck
/**
 * パーティ管理モジュール
 * パーティの編成・保存・読み込みを管理
 * @module twinKey/core/party/PartyManager
 */

import { createMonsterInstance, toBattleUnit } from './MonsterInstance';
import { PARTY_SIZE, getRequiredExp } from '../../data/constants';

/**
 * 新しいパーティを作成
 * @param {string} name - パーティ名
 * @returns {Object} Party
 */
export function createParty(name = '新しいパーティ') {
  return {
    id: `party_${Date.now()}`,
    name,
    members: [], // MonsterInstance の配列（最大5）
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

/**
 * パーティにメンバーを追加
 * @param {Object} party - パーティ
 * @param {Object} card - カード情報
 * @param {Object} progress - 成長データ
 * @returns {{ success: boolean, party?: Object, reason?: string }}
 */
export function addMember(party, card, progress) {
  if (party.members.length >= PARTY_SIZE) {
    return { success: false, reason: 'パーティが満員です' };
  }

  // モンスターカードのみ
  if (card.type !== 'monster') {
    return { success: false, reason: 'モンスターカードのみ編成できます' };
  }

  // 同じカードの重複チェック
  if (party.members.some(m => m.uniqueId === card.uniqueId)) {
    return { success: false, reason: '同じカードは編成できません' };
  }

  const instance = createMonsterInstance(card, progress);

  return {
    success: true,
    party: {
      ...party,
      members: [...party.members, instance],
      updatedAt: Date.now(),
    },
  };
}

/**
 * パーティからメンバーを削除
 * @param {Object} party - パーティ
 * @param {string} uniqueId - 削除するカードのuniqueId
 * @returns {Object} 更新されたパーティ
 */
export function removeMember(party, uniqueId) {
  return {
    ...party,
    members: party.members.filter(m => m.uniqueId !== uniqueId),
    updatedAt: Date.now(),
  };
}

/**
 * パーティメンバーの順序を変更
 * @param {Object} party - パーティ
 * @param {number} fromIndex - 移動元インデックス
 * @param {number} toIndex - 移動先インデックス
 * @returns {Object} 更新されたパーティ
 */
export function reorderMembers(party, fromIndex, toIndex) {
  const members = [...party.members];
  const [removed] = members.splice(fromIndex, 1);
  members.splice(toIndex, 0, removed);

  return {
    ...party,
    members,
    updatedAt: Date.now(),
  };
}

/**
 * パーティメンバーを入れ替え
 * @param {Object} party - パーティ
 * @param {number} slot1 - スロット1
 * @param {number} slot2 - スロット2
 * @returns {Object} 更新されたパーティ
 */
export function swapMembers(party, slot1, slot2) {
  const members = [...party.members];

  // 両方のスロットが範囲内かチェック
  if (slot1 < 0 || slot1 >= members.length || slot2 < 0 || slot2 >= members.length) {
    return party;
  }

  // 入れ替え
  [members[slot1], members[slot2]] = [members[slot2], members[slot1]];

  return {
    ...party,
    members,
    updatedAt: Date.now(),
  };
}

/**
 * パーティをバトル用に変換
 * @param {Object} party - パーティ
 * @returns {Array} BattleUnit の配列
 */
export function partyToBattleUnits(party) {
  return party.members.map((instance, index) => toBattleUnit(instance, index));
}

/**
 * パーティの検証
 * @param {Object} party - パーティ
 * @returns {{ valid: boolean, reason?: string }}
 */
export function validateParty(party) {
  if (!party.members || party.members.length === 0) {
    return { valid: false, reason: 'パーティが空です' };
  }

  if (party.members.length > PARTY_SIZE) {
    return { valid: false, reason: `パーティは${PARTY_SIZE}体までです` };
  }

  // 全員モンスターかチェック
  for (const member of party.members) {
    if (!member.stats || !member.stats.attack) {
      return { valid: false, reason: '無効なメンバーが含まれています' };
    }
  }

  return { valid: true };
}

/**
 * パーティの総合力を計算
 * @param {Object} party - パーティ
 * @returns {number}
 */
export function calculatePartyPower(party) {
  return party.members.reduce((total, member) => {
    return total + member.stats.attack + member.stats.maxHp +
           member.stats.defense * 10 + member.stats.speed * 5;
  }, 0);
}

/**
 * パーティの属性構成を取得
 * @param {Object} party - パーティ
 * @returns {Object} 属性ごとの人数
 */
export function getAttributeComposition(party) {
  const composition = {};
  for (const member of party.members) {
    const attr = member.attribute;
    composition[attr] = (composition[attr] || 0) + 1;
  }
  return composition;
}

/**
 * 経験値を加算してレベルアップ処理
 * @param {Object} monsterProgress - 現在の成長データ
 * @param {number} expAmount - 獲得経験値
 * @param {string} rarity - レアリティ
 * @returns {{ level: number, exp: number, leveledUp: boolean, levelsGained: number }}
 */
export function addExperience(monsterProgress, expAmount, rarity) {
  const { RARITY_CONFIG } = require('../../data/constants');
  const maxLevel = RARITY_CONFIG[rarity]?.maxLevel || 10;

  let newExp = (monsterProgress?.exp || 0) + expAmount;
  let newLevel = monsterProgress?.level || 1;
  const startLevel = newLevel;

  // レベルアップ処理
  while (newLevel < maxLevel) {
    const required = getRequiredExp(newLevel);
    if (newExp >= required) {
      newExp -= required;
      newLevel++;
    } else {
      break;
    }
  }

  // 最大レベルに達したら経験値は0
  if (newLevel >= maxLevel) {
    newLevel = maxLevel;
    newExp = 0;
  }

  return {
    level: newLevel,
    exp: newExp,
    awakening: monsterProgress?.awakening || 0,
    leveledUp: newLevel > startLevel,
    levelsGained: newLevel - startLevel,
  };
}

/**
 * パーティのステータスを取得
 * @param {Object} party - パーティ
 * @returns {Object} ステータス情報
 */
export function getPartyStats(party) {
  return getPartySummary(party);
}

/**
 * パーティ情報のサマリーを取得
 * @param {Object} party - パーティ
 * @returns {Object} サマリー情報
 */
export function getPartySummary(party) {
  if (!party.members || party.members.length === 0) {
    return {
      memberCount: 0,
      totalPower: 0,
      averageLevel: 0,
      attributes: {},
    };
  }

  const totalPower = calculatePartyPower(party);
  const averageLevel = Math.floor(
    party.members.reduce((sum, m) => sum + m.level, 0) / party.members.length
  );
  const attributes = getAttributeComposition(party);

  return {
    memberCount: party.members.length,
    totalPower,
    averageLevel,
    attributes,
  };
}
